import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { TextArea } from '../../../../components/workbook/forms';

export default function Worksheet1_2Page() {
  const [rewrite, setRewrite] = useState('');

  const checklist = [
    {
      criterion: 'Singular Unknown',
      question: 'Could this be answered in one sentence? If not, split it.',
      checked: false
    },
    {
      criterion: 'Decision-Linked',
      question: 'If we knew this, what would we do differently tomorrow?',
      checked: false
    },
    {
      criterion: 'Neutral & Investigable',
      question: 'Does this invite discovery—or argue a point?',
      checked: false
    }
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      <header className="bg-[var(--slate-dim)] border-b border-[var(--slate)]">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link
            to="/elevation/workbook/oqf/module-1"
            className="text-[var(--text-sec)] hover:text-[var(--text-pri)] text-sm mb-2 inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Module 1
          </Link>
          <h1 className="text-2xl font-bold text-[var(--text-pri)]">
            ✍️ Activity: Worksheet 1.2
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-[var(--surface)] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-3">
            Instructions
          </h2>
          <p className="text-[var(--text-sec)]">
            The central "bad" question everyone in the "Pro Tier" scenario is <em>really</em> asking is below. Use the 3-Point Checklist to rewrite it into a single, decision-linked, and neutral "OQF-Ready" question.
          </p>
        </div>

        <div className="bg-[var(--coral-dim)] border-l-4 border-[var(--coral)] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-2">
            The "Bad" Question:
          </h2>
          <p className="text-lg text-[var(--text-pri)] italic">
            "Should we go ahead and launch the Pro tier next quarter?"
          </p>
        </div>

        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
            3-Point OQF Checklist
          </h2>
          <div className="space-y-4 mb-6">
            {checklist.map((item, index) => (
              <div key={index} className="border border-[var(--rule)] p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[var(--slate)] flex items-center justify-center mt-0.5">
                    <span className="text-xs font-bold text-[var(--text-ter)]">{index + 1}</span>
                  </div>
                  <div className="flex-grow">
                    <h3 className="font-semibold text-[var(--text-pri)] mb-1">
                      {item.criterion}
                    </h3>
                    <p className="text-sm text-[var(--text-sec)] italic">
                      {item.question}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4 mb-6">
            <p className="text-sm text-[var(--text-sec)]">
              <strong>Hint:</strong> Ask yourself: What's the <em>real</em> unknown that needs to be true for this launch to succeed?
            </p>
          </div>

          <div>
            <TextArea
              label='Your "OQF-Ready" Rewrite:'
              value={rewrite}
              onChange={(e) => setRewrite(e.target.value)}
              placeholder="Write your rewritten question here..."
              rows={4}
            />
          </div>
        </div>

        <div className="bg-[var(--green-dim)] border border-[var(--green)] p-4 mb-6">
          <h3 className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-2 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Model Answer (for reference)
          </h3>
          <p className="text-[var(--green)] dark:text-[var(--text-ter)] mb-2">
            <strong>Question:</strong> "What evidence would show that a Pro tier solves a problem customers are willing to pay more to fix?"
          </p>
          <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
            This question is singular (focuses on evidence of value), decision-linked (the answer determines whether to proceed), and neutral (invites discovery rather than arguing a point).
          </p>
        </div>

        <div className="flex justify-between items-center">
          <Link
            to="/elevation/workbook/oqf/module-1/worksheet-1-1"
            className="px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </Link>
          <Link
            to="/elevation/workbook/oqf/module-2"
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] inline-flex items-center gap-2"
          >
            Next: Module 2
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

